import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { api } from "../lib/api";
import { useSite } from "../lib/SiteContext";
import Footer from "../components/Footer";
import { fmtNewsDate } from "../components/NewsSection";

const EASE = [0.16, 1, 0.3, 1];

export default function News() {
  const { settings } = useSite();
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    api.get("/news").then((r) => setPosts(r.data)).catch(() => setPosts([]));
  }, []);

  return (
    <main data-testid="news-page" className="min-h-screen pt-32 md:pt-44">
      <div className="mx-auto max-w-[1100px] px-6 md:px-10 pb-24 md:pb-32">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE }}
        >
          <Link
            to="/"
            data-testid="news-back-link"
            className="link-line inline-flex items-center gap-2 font-meta text-[10px] uppercase tracking-[0.3em] text-mute hover:text-ochre transition-colors duration-300"
          >
            <ArrowLeft size={12} /> Back to the Gallery
          </Link>
          <div className="flex items-center gap-4 mt-10 mb-6">
            <span className="h-px w-12 rule-flare" />
            <p className="eyebrow !text-ochre/90">From the Studio</p>
          </div>
          <h1 className="font-display font-light text-5xl md:text-7xl leading-[1.05]">
            News &amp; Upcoming Shows
          </h1>
        </motion.div>

        {posts !== null && posts.length === 0 && (
          <div className="mt-16 border border-dashed border-white/15 py-24 text-center" data-testid="news-empty-state">
            <p className="font-display italic text-2xl text-bone/80">Nothing to report yet.</p>
            <p className="font-meta text-[10px] uppercase tracking-[0.3em] text-mute mt-3">
              The studio is quietly at work — check back soon
            </p>
          </div>
        )}

        <div className="mt-16 md:mt-24">
          {(posts || []).map((p, i) => (
            <motion.article
              key={p.id}
              data-testid={`news-item-${p.id}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.8, ease: EASE, delay: Math.min(i, 3) * 0.06 }}
              className="grid md:grid-cols-[220px_1fr] gap-4 md:gap-12 border-t border-line py-10 md:py-14"
            >
              <p className="font-meta text-[10px] uppercase tracking-[0.3em] text-gold pt-2">
                {fmtNewsDate(p)}
              </p>
              <div>
                <h2 className="font-display text-2xl md:text-4xl leading-snug">{p.title}</h2>
                {p.body && (
                  <p className="text-sm md:text-base leading-relaxed text-mute mt-5 whitespace-pre-line">
                    {p.body}
                  </p>
                )}
              </div>
            </motion.article>
          ))}
        </div>
      </div>
      <Footer settings={settings} />
    </main>
  );
}
